import { useCallback, useEffect, useRef } from 'react'

import { get } from 'lodash'

import { ALL } from '../../constants'
import { ISearchConfiguration, IallResultsLimits } from '../../types'
import {
  BLUR_INPUT,
  CLEAR_INPUT,
  CLOSE_RESULTS,
  FOCUS_INPUT,
  INITIALIZE,
  SEARCH_BEGIN,
  SEARCH_FAILURE,
  SEARCH_SUCCESS,
  SELECT_RESULT,
  SET_RESULT_TYPE,
  UPDATE_CONFIGS,
} from './action-types'
import { onSearch } from './on-search'
import { IAction, IState, TDispatch } from './types'

interface IInitAccumulator {
  configs: ISearchConfiguration[]
  allResultsLimits: IallResultsLimits
  tabLabels: string[]
  placeholderOptions: string[]
  allResultsOrder?: string[]
}

/**
 * updateConfigs: to update the configs in state when the configs prop changes
 * Different data are accumulated by looping through the configs
 */
const updateConfigs = (configs: ISearchConfiguration[]) => {
  return configs.reduce(
    (acc, config) => {
      const { hidden, label, allLimit, placeholderOption, hideTab } = config
      acc.configs.push(config)
      if (!hidden && !hideTab) {
        acc.tabLabels.push(label)
        acc.allResultsLimits[label] = allLimit
        if (placeholderOption) {
          acc.placeholderOptions.push(placeholderOption)
        }
      }
      return acc
    },
    {
      configs: [],
      allResultsLimits: {},
      tabLabels: [ALL],
      placeholderOptions: [],
    } as IInitAccumulator,
  )
}

const initialize = (configs: ISearchConfiguration[], allResultsOrder: string[]) => {
  const updatedConfigs = updateConfigs(configs)
  updatedConfigs.allResultsOrder = allResultsOrder
  return updatedConfigs
}

/**
 * useInitialize: to initialize the state when the component mounts
 */
export const useInitialize = (dispatch: TDispatch, configs: ISearchConfiguration[], allResultsOrder: string[]) => {
  const hasInitialized = useRef(false)

  useEffect(() => {
    // Update the configs in state when the configs prop changes
    // does not run on first render
    // Sometimes they need to update themselves on init asynchronously so we have to let them change
    if (!hasInitialized.current) {
      return
    }
    dispatch({
      type: UPDATE_CONFIGS,
      payload: updateConfigs(configs),
    })
  }, [dispatch, configs])

  useEffect(() => {
    // Initialize the state when the component mounts
    // Only run on first render
    if (hasInitialized.current) {
      return
    }
    dispatch({
      type: INITIALIZE,
      payload: initialize(configs, allResultsOrder),
    })
    hasInitialized.current = true
  }, [dispatch, configs, allResultsOrder])
}

/**
 * useOnFocus: to open the focusDropdown or not when the user clicks on the input
 */
export const useOnFocus = (dispatch: React.Dispatch<IAction>) => {
  return useCallback(() => {
    dispatch({ type: FOCUS_INPUT })
  }, [dispatch])
}

/**
 * useOnBlur: to close the results when the user clicks outside the input
 */
export const useOnBlur = (dispatch: React.Dispatch<IAction>) => {
  return useCallback(() => {
    dispatch({ type: BLUR_INPUT })
  }, [dispatch])
}

/**
 * useOnClear: to clear the input when the user clicks the clear button
 */
export const useOnClear = (dispatch: React.Dispatch<IAction>) => {
  return useCallback(() => {
    dispatch({ type: CLEAR_INPUT })
  }, [dispatch])
}

/**
 * useOnCloseResults: to close the results when the user presses escape
 */
export const useOnCloseResults = (dispatch: React.Dispatch<IAction>) => {
  return useCallback(() => {
    dispatch({ type: CLOSE_RESULTS })
  }, [dispatch])
}

/**
 * useOnInputChange: to update the results in state when the user types in the input
 */
export const useOnInputChange = (dispatch: React.Dispatch<IAction>, state: IState) => {
  return useCallback(
    (searchTerm: string) => {
      dispatch({ type: SEARCH_BEGIN, payload: { searchTerm } })
      return onSearch(searchTerm, state)
        .then((resultsByType) => {
          dispatch({ type: SEARCH_SUCCESS, payload: { resultsByType, searchTerm } })
        })
        .catch((error) => {
          dispatch({ type: SEARCH_FAILURE, payload: { error, searchTerm } })
        })
    },
    [dispatch, state],
  )
}

/**
 * useOnChangeResultType: to update the resultType in state when the user clicks on a tab
 */
export const useOnChangeResultType = (dispatch: React.Dispatch<IAction>) => {
  return useCallback(
    (resultType: string) => {
      dispatch({ type: SET_RESULT_TYPE, payload: { resultType } })
    },
    [dispatch],
  )
}

/**
 * useOnChange: to click the result when the user selects it with either click or keyboard
 */
export const useOnChange = (dispatch: React.Dispatch<IAction>) => {
  return useCallback(
    (event, value: any, reason: string) => {
      // All results need to use the ID provided to the ResultComponent as a prop
      // This way we can click it for the user when they select it with a keyboard
      const id: string = get(value, 'id')
      if (id && reason === 'select-option') {
        if (event.type !== 'click') {
          document.getElementById(id)?.click()
        }
        setTimeout(() => {
          dispatch({ type: SELECT_RESULT, payload: value })
        }, 10)
        return
      }
    },
    [dispatch],
  )
}
